import React, { useState } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Breadcrumbs, 
  Link, 
  IconButton, 
  Divider, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  Grid, 
  FormControlLabel, 
  Switch, 
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  AppBar,
  Toolbar,
  Slide,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import { 
  Home as HomeIcon, 
  ChevronRight, 
  Menu as MenuIcon, 
  Add, 
  Edit, 
  Delete, 
  Close,
  CheckCircle,
  Cancel,
  Refresh,
  Sync
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { db } from './Dexie';
import { useLiveQuery } from 'dexie-react-hooks';
import { log } from '../services/loggingService';
import { syncTechyShopMenu, syncTechyShopVociMenu } from '../services/menuService';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function TechyShopMenu() {
  const navigate = useNavigate();
  
  // Modals state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isVoceModalOpen, setIsVoceModalOpen] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [selectedVoce, setSelectedVoce] = useState(null);
  
  // Menu form state
  const [formData, setFormData] = useState({
    id: '',
    nome: '',
    posizione: '',
    enable: true,
    created_at: '',
    updated_at: ''
  });

  // Voce Menu form state
  const [voceFormData, setVoceFormData] = useState({
    id: '',
    menu_id: '',
    testo: '',
    link: '',
    tipo: '',
    id_riferimento: '',
    voce_padre_id: '',
    ordinamento: 0,
    target_blank: false
  });
  
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingVoce, setIsSavingVoce] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isSyncingVoci, setIsSyncingVoci] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  // Ottieni menu in tempo reale dal DB
  const menus = useLiveQuery(() => db.techyshop_menu.toArray(), []);

  // Ottieni voci menu filtrate per il menu selezionato
  const vociMenu = useLiveQuery(
    () => selectedMenu ? db.techyshop_voci_menu.where('menu_id').equals(selectedMenu.id).toArray() : [],
    [selectedMenu]
  );

  const handleResetMenu = async () => {
    if (!window.confirm("Sei sicuro di voler resettare tutti i menu? Questa azione cancellerà i dati locali e li riscaricherà dal server.")) {
      return;
    }

    setIsResetting(true);
    try {
      const result = await syncTechyShopMenu(true);
      setToast({ 
        open: true, 
        message: result.success ? result.message : `Errore: ${result.message}`, 
        severity: result.success ? 'success' : 'error' 
      });
    } catch (error) {
      log("Errore durante il reset dei menu:", 'ERROR', error);
      setToast({ open: true, message: `Errore: ${error.message}`, severity: 'error' });
    } finally {
      setIsResetting(false);
    }
  };

  const handleOpenModal = (menu = null) => {
    const now = new Date();
    const mysqlTimestamp = now.toISOString().slice(0, 19).replace('T', ' ');

    if (menu) {
      setSelectedMenu(menu);
      setFormData({
        id: menu.id || '',
        nome: menu.nome || '',
        posizione: menu.posizione || '',
        enable: menu.enable === 1 || menu.enable === true,
        created_at: menu.created_at || mysqlTimestamp,
        updated_at: menu.updated_at || mysqlTimestamp
      });
    } else {
      setSelectedMenu(null);
      setFormData({
        id: '',
        nome: '',
        posizione: '',
        enable: true,
        created_at: mysqlTimestamp,
        updated_at: mysqlTimestamp
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedMenu(null);
  };

  const handleOpenVoceModal = (voce = null) => {
    if (voce) {
      setSelectedVoce(voce);
      setVoceFormData({
        id: voce.id || '',
        menu_id: voce.menu_id || selectedMenu?.id || '',
        testo: voce.testo || '',
        link: voce.link || '',
        tipo: voce.tipo || '',
        id_riferimento: voce.id_riferimento !== null && voce.id_riferimento !== undefined ? voce.id_riferimento : '',
        voce_padre_id: voce.voce_padre_id !== null && voce.voce_padre_id !== undefined ? voce.voce_padre_id : '',
        ordinamento: voce.ordinamento || 0,
        target_blank: voce.target_blank === 1 || voce.target_blank === true
      });
    } else {
      setSelectedVoce(null);
      setVoceFormData({
        id: '',
        menu_id: selectedMenu?.id || '',
        testo: '',
        link: '',
        tipo: '',
        id_riferimento: '',
        voce_padre_id: '',
        ordinamento: 0,
        target_blank: false
      });
    }
    setIsVoceModalOpen(true);
  };

  const handleCloseVoceModal = () => {
    setIsVoceModalOpen(false);
    setSelectedVoce(null);
  };

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleVoceInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    setVoceFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSaveMenu = async () => {
    if (!formData.nome.trim()) {
      setToast({ open: true, message: 'Errore: Il nome è obbligatorio.', severity: 'error' });
      return;
    }

    setIsSaving(true);
    
    const now = new Date();
    const mysqlTimestamp = now.toISOString().slice(0, 19).replace('T', ' ');

    const menuData = {
      ...formData,
      enable: formData.enable ? 1 : 0,
      updated_at: mysqlTimestamp
    };

    try {
      if (selectedMenu) {
        // Logica di modifica
        const values = Object.entries(menuData)
          .filter(([key, value]) => key !== 'id' && value !== undefined && value !== null)
          .map(([key, value]) => ({ field: key, value }));

        const filters = [{ field: 'id', value: selectedMenu.id }];

        const response = await fetch('https://deadapi.techysumo.it/techyshop/editMenu', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': process.env.REACT_APP_API_KEY,
          },
          body: JSON.stringify({ values, filters }),
        });

        const result = await response.json();

        if (result.result) {
          await db.techyshop_menu.update(selectedMenu.id, menuData);
          setToast({ open: true, message: 'Menu aggiornato con successo!', severity: 'success' });
          handleCloseModal();
        } else {
          throw new Error(result.text || "Errore durante l'aggiornamento del menu sul server");
        }
      } else {
        // Logica di aggiunta
        const { id, ...payload } = menuData;
        
        const response = await fetch('https://deadapi.techysumo.it/techyshop/addMenu', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': process.env.REACT_APP_API_KEY,
          },
          body: JSON.stringify(payload),
        });

        const result = await response.json();

        if (result.result) {
          const newId = result.details?.id || result.details || Date.now();
          await db.techyshop_menu.add({ ...menuData, id: newId });
          setToast({ open: true, message: 'Menu creato con successo!', severity: 'success' });
          handleCloseModal();
        } else {
          throw new Error(result.text || "Errore durante la creazione del menu sul server");
        }
      }
    } catch (error) {
      log("Errore nel salvataggio del menu:", 'ERROR', error);
      setToast({ open: true, message: `Errore: ${error.message}`, severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveVoce = async () => {
    if (!voceFormData.testo.trim()) {
      setToast({ open: true, message: 'Errore: Il testo è obbligatorio.', severity: 'error' });
      return;
    }

    setIsSavingVoce(true);
    
    try {
      let result;
      let finalId = voceFormData.id;

      if (selectedVoce) {
        // Logica di MODIFICA (editVociMenu)
        const filters = [{ field: 'id', value: selectedVoce.id }];
        
        // Determiniamo quali campi sono stati modificati
        const values = [];
        const fieldsToTrack = ['testo', 'link', 'tipo', 'id_riferimento', 'voce_padre_id', 'ordinamento', 'target_blank'];
        
        fieldsToTrack.forEach(field => {
          let newValue = voceFormData[field];
          let oldValue = selectedVoce[field];
          
          if (field === 'target_blank') {
            newValue = newValue ? 1 : 0;
            oldValue = (oldValue === 1 || oldValue === true) ? 1 : 0;
          }
          
          // Funzione di utilità per normalizzare i valori prima del confronto
          const normalize = (val) => {
            if (val === null || val === undefined || val === '') return '';
            return String(val);
          };

          if (normalize(newValue) !== normalize(oldValue)) {
            values.push({ field: field, value: newValue });
          }
        });

        // Se non ci sono modifiche, non chiamiamo l'API
        if (values.length === 0) {
          setToast({ open: true, message: 'Nessuna modifica rilevata.', severity: 'info' });
          handleCloseVoceModal();
          setIsSavingVoce(false);
          return;
        }

        const response = await fetch('https://deadapi.techysumo.it/techyshop/editVociMenu', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': process.env.REACT_APP_API_KEY,
          },
          body: JSON.stringify({ filters, values }),
        });

        result = await response.json();
      } else {
        // Logica di AGGIUNTA (addVociMenu)
        const payload = {
          ...voceFormData,
          target_blank: voceFormData.target_blank ? 1 : 0
        };
        delete payload.id;

        const response = await fetch('https://deadapi.techysumo.it/techyshop/addVociMenu', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': process.env.REACT_APP_API_KEY,
          },
          body: JSON.stringify(payload),
        });

        result = await response.json();
        
        // Determiniamo l'ID corretto restituito dal server
        if (result.result && result.details) {
          if (typeof result.details === 'object' && result.details.id) {
            finalId = result.details.id;
          } else if (typeof result.details === 'number' || typeof result.details === 'string') {
            finalId = result.details;
          }
        }
        if (!finalId) finalId = Date.now();
      }

      if (result.result) {
        const numericId = parseInt(finalId);
        const cleanedId = isNaN(numericId) ? String(finalId) : numericId;

        const finalVoce = { 
          ...voceFormData, 
          target_blank: voceFormData.target_blank ? 1 : 0,
          id: cleanedId 
        };
        
        await db.techyshop_voci_menu.put(finalVoce);
        setToast({ open: true, message: 'Voce menu salvata con successo!', severity: 'success' });
        handleCloseVoceModal();
      } else {
        throw new Error(result.text || "Errore durante il salvataggio della voce menu sul server");
      }
    } catch (error) {
      log("Errore nel salvataggio della voce menu:", 'ERROR', error);
      setToast({ open: true, message: `Errore: ${error.message}`, severity: 'error' });
    } finally {
      setIsSavingVoce(false);
    }
  };

  const handleDeleteVoce = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa voce menu?')) return;

    try {
      const response = await fetch('https://deadapi.techysumo.it/techyshop/delVociMenu', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': process.env.REACT_APP_API_KEY,
        },
        body: JSON.stringify({ id }),
      });

      const result = await response.json();

      if (result.result) {
        await db.techyshop_voci_menu.delete(id);
        setToast({ open: true, message: 'Voce menu eliminata con successo!', severity: 'info' });
      } else {
        throw new Error(result.text || "Errore durante l'eliminazione della voce menu sul server");
      }
    } catch (error) {
      log("Errore nell'eliminazione della voce menu:", 'ERROR', error);
      setToast({ open: true, message: `Errore: ${error.message}`, severity: 'error' });
    }
  };

  const handleDeleteMenu = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo menu?')) return;

    try {
      const filters = [{ field: 'id', value: id }];

      const response = await fetch('https://deadapi.techysumo.it/techyshop/delMenu', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': process.env.REACT_APP_API_KEY,
        },
        body: JSON.stringify({ filters }),
      });

      const result = await response.json();

      if (result.result) {
        await db.techyshop_menu.delete(id);
        setToast({ open: true, message: 'Menu eliminato con successo!', severity: 'info' });
      } else {
        throw new Error(result.text || "Errore durante l'eliminazione del menu sul server");
      }
    } catch (error) {
      log("Errore nell'eliminazione del menu:", 'ERROR', error);
      setToast({ open: true, message: `Errore: ${error.message}`, severity: 'error' });
    }
  };

  const handleSyncVociMenu = async () => {
    if (!selectedMenu) return;
    
    setIsSyncingVoci(true);
    try {
      const result = await syncTechyShopVociMenu(selectedMenu.id);
      setToast({ 
        open: true, 
        message: result.success ? result.message : `Errore: ${result.message}`, 
        severity: result.success ? 'success' : 'error' 
      });
    } catch (error) {
      log("Errore durante la sincronizzazione delle voci menu:", 'ERROR', error);
      setToast({ open: true, message: `Errore: ${error.message}`, severity: 'error' });
    } finally {
      setIsSyncingVoci(false);
    }
  };

  const handleToastClose = () => setToast({ ...toast, open: false });

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Breadcrumbs & Navigation */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Breadcrumbs
          separator={<ChevronRight fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ color: 'rgba(255,255,255,0.7)' }}
        >
          <Link
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: 'inherit' }}
            onClick={() => navigate('/')}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Link
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: 'inherit' }}
            onClick={() => navigate('/techyshop')}
          >
            TechyShop
          </Link>
          <Typography color="primary.main" sx={{ fontWeight: 600 }}>Gestione Menu</Typography>
        </Breadcrumbs>

        <IconButton color="primary" onClick={() => navigate('/techyshop')} sx={{ border: '1px solid', borderColor: 'primary.main' }}>
          <MenuIcon />
        </IconButton>
      </Box>

      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 800 }}>
            Gestione <Box component="span" sx={{ color: 'primary.main' }}>Menu</Box>
          </Typography>

          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            In questa sezione potrai configurare i menu dello shop.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="outlined" 
            color="primary" 
            startIcon={isResetting ? <CircularProgress size={20} /> : <Refresh />} 
            onClick={handleResetMenu}
            disabled={isSaving || isResetting}
          >
            Reset Menu
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<Add />} 
            onClick={() => handleOpenModal()}
            disabled={isSaving || isResetting}
          >
            Nuovo Menu
          </Button>
        </Box>
      </Box>

      {/* Menu Table */}
      <TableContainer component={Paper} sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>ID</TableCell>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>Nome</TableCell>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>Posizione</TableCell>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }} align="center">Stato</TableCell>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }} align="right">Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {menus?.map((m) => (
              <TableRow 
                key={m.id} 
                hover 
                onClick={() => handleOpenModal(m)}
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' } 
                }}
              >
                <TableCell sx={{ color: 'rgba(255,255,255,0.7)' }}>{m.id}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>{m.nome}</TableCell>
                <TableCell sx={{ color: 'rgba(255,255,255,0.7)' }}>{m.posizione}</TableCell>
                <TableCell align="center">
                  <Tooltip title={m.enable === 1 || m.enable === true ? "Attivo" : "Non Attivo"}>
                    {m.enable === 1 || m.enable === true ? <CheckCircle style={{ color: '#4dab56' }} fontSize="small" /> : <Cancel style={{ color: '#f44336' }} fontSize="small" />}
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  <IconButton 
                    size="small" 
                    color="primary" 
                    onClick={(e) => { e.stopPropagation(); handleOpenModal(m); }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    color="error" 
                    onClick={(e) => { e.stopPropagation(); handleDeleteMenu(m.id); }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {(!menus || menus.length === 0) && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                    Nessun menu trovato.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modal Full Screen Aggiungi/Modifica Menu */}
      <Dialog 
        fullScreen 
        open={isModalOpen} 
        onClose={handleCloseModal} 
        TransitionComponent={Transition}
      >
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleCloseModal}
              aria-label="close"
            >
              <Close />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              {selectedMenu ? `Modifica Menu: ${selectedMenu.nome}` : 'Aggiungi Nuovo Menu'}
            </Typography>
            <Button autoFocus color="inherit" onClick={handleSaveMenu} disabled={isSaving}>
              {isSaving ? <CircularProgress size={24} color="inherit" /> : 'Salva'}
            </Button>
          </Toolbar>
        </AppBar>
        <DialogContent sx={{ p: 4, backgroundColor: '#121212' }}>
          <Container maxWidth="md">
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, color: 'primary.main' }}>
              Informazioni Generali
            </Typography>
            <Grid container spacing={3} sx={{ mb: 6 }}>
              {selectedMenu && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="ID"
                    name="id"
                    value={formData.id}
                    disabled
                  />
                </Grid>
              )}
              <Grid item xs={12} md={selectedMenu ? 6 : 12}>
                <TextField
                  fullWidth
                  label="Nome"
                  name="nome"
                  value={formData.nome}
                  onChange={handleInputChange}
                  disabled={isSaving}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Posizione"
                  name="posizione"
                  value={formData.posizione}
                  onChange={handleInputChange}
                  disabled={isSaving}
                  placeholder="es: header, footer, sidebar"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Switch 
                        name="enable" 
                        checked={formData.enable} 
                        onChange={handleInputChange} 
                        color="primary" 
                      />
                    }
                    label="Menu Abilitato"
                    disabled={isSaving}
                  />
                </Box>
              </Grid>
            </Grid>

            {selectedMenu && (
              <>
                <Divider sx={{ mb: 6, borderColor: 'rgba(255,255,255,0.1)' }} />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    Gestione Voci Menu
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button 
                      variant="contained" 
                      color="primary" 
                      startIcon={<Add />}
                      onClick={() => handleOpenVoceModal()}
                    >
                      Aggiungi voce
                    </Button>
                    <Button 
                      variant="outlined" 
                      color="primary" 
                      startIcon={isSyncingVoci ? <CircularProgress size={20} /> : <Sync />}
                      onClick={handleSyncVociMenu}
                      disabled={isSyncingVoci}
                    >
                      Sincronizza voci menu
                    </Button>
                  </Box>
                </Box>

                <TableContainer component={Paper} sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', mb: 4 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>ID</TableCell>
                        <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>Testo</TableCell>
                        <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>Link</TableCell>
                        <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>Voce Padre</TableCell>
                        <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }} align="right">Ordine</TableCell>
                        <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }} align="right">Azioni</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {vociMenu?.map((v) => {
                        const vocePadre = vociMenu.find(vp => String(vp.id) === String(v.voce_padre_id));
                        return (
                          <TableRow key={v.id} hover onClick={() => handleOpenVoceModal(v)} sx={{ cursor: 'pointer' }}>
                            <TableCell sx={{ color: 'rgba(255,255,255,0.7)' }}>{v.id}</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 600 }}>{v.testo}</TableCell>
                            <TableCell sx={{ color: 'rgba(255,255,255,0.7)' }}>{v.link}</TableCell>
                            <TableCell sx={{ color: 'rgba(255,255,255,0.7)' }}>{vocePadre ? vocePadre.testo : '-'}</TableCell>
                            <TableCell align="right" sx={{ color: 'rgba(255,255,255,0.7)' }}>{v.ordinamento}</TableCell>
                            <TableCell align="right">
                              <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); handleOpenVoceModal(v); }}>
                                <Edit fontSize="small" />
                              </IconButton>
                              <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDeleteVoce(v.id); }}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {(!vociMenu || vociMenu.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                              Nessuna voce menu trovata. Sincronizza per scaricarle.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Container>
        </DialogContent>
      </Dialog>

      {/* Modal Gestione Voci Menu */}
      <Dialog open={isVoceModalOpen} onClose={handleCloseVoceModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {selectedVoce ? 'Modifica Voce Menu' : 'Aggiungi Voce Menu'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="ID"
                name="id"
                value={voceFormData.id}
                disabled
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="ID Menu"
                name="menu_id"
                value={voceFormData.menu_id}
                disabled
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Testo"
                name="testo"
                value={voceFormData.testo}
                onChange={handleVoceInputChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Link"
                name="link"
                value={voceFormData.link}
                onChange={handleVoceInputChange}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Tipo"
                name="tipo"
                value={voceFormData.tipo}
                onChange={handleVoceInputChange}
                placeholder="es: category, product, static"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="ID Riferimento"
                name="id_riferimento"
                value={voceFormData.id_riferimento}
                onChange={handleVoceInputChange}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel id="voce-padre-label">Voce Padre</InputLabel>
                <Select
                  labelId="voce-padre-label"
                  label="Voce Padre"
                  name="voce_padre_id"
                  value={voceFormData.voce_padre_id}
                  onChange={handleVoceInputChange}
                >
                  <MenuItem value=""><em>Nessuna (Voce Principale)</em></MenuItem>
                  {vociMenu
                    ?.filter(v => !selectedVoce || String(v.id) !== String(selectedVoce.id))
                    .map(v => (
                      <MenuItem key={v.id} value={v.id}>{v.testo}</MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Ordinamento"
                name="ordinamento"
                value={voceFormData.ordinamento}
                onChange={handleVoceInputChange}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch 
                    name="target_blank" 
                    checked={voceFormData.target_blank} 
                    onChange={handleVoceInputChange} 
                    color="primary" 
                  />
                }
                label="Apri in nuova scheda (target blank)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseVoceModal} disabled={isSavingVoce}>Annulla</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveVoce} 
            disabled={isSavingVoce}
          >
            {isSavingVoce ? <CircularProgress size={24} /> : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleToastClose} severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>

      <Box sx={{ mt: 8 }}>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 3 }} />
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center' }}>
          Sistema integrato TechySumo • Ultimo aggiornamento: Aprile 2026
        </Typography>
      </Box>
    </Container>
  );
}

export default TechyShopMenu;
